import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  Container,
  Spinner,
} from "reactstrap";
import userServices from "services/userServices";
import { useTranslation } from "context/TranslationProvider";
import { useNotification } from "providers/NotificationProvider";
import helpers from "utils/helpers";

const RolePermissionPage = () => {
  const { roleId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notify } = useNotification();

  const isNewRole = !roleId;

  const [roleName, setRoleName] = useState("");
  const [slug, setSlug] = useState("");
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchRole = async () => {
      if (isNewRole) {
        setLoading(false);
        return;
      }

      try {
        const res = await userServices.getRoleById(roleId);
        const roleData = res?.data?.role || res?.role || res?.data;
        if (roleData) {
          setRoleName(roleData.name || "");
          setPermissions(roleData.permissions?.modules || {});
        } else {
          notify("error", t("page.roles.fetchFailed", "Failed to fetch role details"));
        }
      } catch (error) {
        console.error(error);
        notify("error", t("page.roles.fetchFailed", "Failed to fetch role details"));
      } finally {
        setLoading(false);
      }
    };

    fetchRole();
  }, [roleId, isNewRole, notify, t]);

  const toggleModule = (moduleKey, perms) => {
    setPermissions((prev) => {
      const updated = { ...prev };
      if (updated[moduleKey]) {
        delete updated[moduleKey];
      } else {
        updated[moduleKey] = {};
        perms.forEach((p) => (updated[moduleKey][p] = true));
      }
      return updated;
    });
  };

  const togglePermission = (moduleKey, perm) => {
    setPermissions((prev) => ({
      ...prev,
      [moduleKey]: {
        ...prev[moduleKey],
        [perm]: !prev[moduleKey]?.[perm],
      },
    }));
  };

  const handleSave = async () => {
    if (!roleName.trim()) {
      return notify("error", `${t("page.roles.name", "Name")} ${t("page.roles.required", "is required")}`);
    }

    if (isNewRole && !slug.trim()) {
      return notify("error", `${t("page.roles.role", "Role")} ${t("page.roles.required", "is required")}`);
    }

    setIsSaving(true);

    try {
      let response;

      if (isNewRole) {
        const payload = {
          name: helpers.capitalizeFirstLetter(roleName.trim()),
          slug: slug.toLowerCase(),
          permissions: { modules: permissions },
        };
        response = await userServices.createRole(payload);
      } else {
        const payload = { name: roleName, modules: permissions };
        response = await userServices.updateRolePermissions(roleId, payload);
      }

      if (response?.success) {
        const defaultSuccessMsg = isNewRole ? t("page.roles.createRoleSuccess", "Role created successfully") : t("page.roles.updatePermissionsSuccess", "Permissions updated successfully");
        notify(
          "success",
          t(response?.messageKey || (isNewRole ? "page.roles.createRoleSuccess" : "page.roles.updatePermissionsSuccess"), response?.message || defaultSuccessMsg)
        );
        navigate("/admin/role");
      } else {
        notify("error", t(response?.error || response?.messageKey || "page.roles.saveFailed", response?.message || t("page.roles.saveFailed", "Failed to save role")));
      }
    } catch (error) {
      console.error(error);
      notify("error", t("page.roles.saveFailed", "Failed to save role"));
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="center-loader">
        <div className="center-loader-content">
          <Spinner/>
          <p className="mt-3 text-muted">
            {t("common.loading", "Loading....")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Card className="shadow rounded">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-3">
            <h3 className="mb-0">
              {isNewRole ? t("page.roles.addRole", "Add Role") : t("page.roles.editRole", "Edit Role")}
            </h3>
            <Button color="secondary" size="sm" onClick={() => navigate(-1)}>
              {t("page.roles.actions.back", "Back")}
            </Button>
          </div>

          <Row>
            <Col md="4">
              <FormGroup>
                <Label>
                  {t("page.roles.name", "Name")} <span className="text-danger">*</span>
                </Label>
                <Input
                  placeholder={t("page.roles.name", "Name")}
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                />
              </FormGroup>

              {isNewRole && (
                <FormGroup>
                  <Label>
                    {t("page.roles.role", "Role")} <span className="text-danger">*</span>
                  </Label>
                  <Input
                    placeholder={t("page.roles.role", "Role")}
                    value={slug}
                    onChange={(e) =>
                      setSlug(
                        e.target.value
                          .toLowerCase()
                          .replace(/\s+/g, "_")
                          .replace(/[^a-z0-9_]/g, "")
                      )
                    }
                  />
                </FormGroup>
              )}

              <Button color="primary" onClick={handleSave} className="mb-4" disabled={isSaving}>
                {isSaving && <Spinner size="sm" className="mr-2" />}
                {isNewRole ? t("page.roles.createRole", "Create Role") : t("page.roles.updatePermissions", "Update Permissions")}
              </Button>
            </Col>

            <Col md="8">
              {helpers.RESOURCES_LIST.map((res) => {
                const enabled = !!permissions[res.key];

                return (
                  <div key={res.key} className="mb-3 border rounded p-3">
                    <div className="d-flex align-items-center gap-2">
                      <Input
                        type="checkbox"
                        checked={enabled}
                        onChange={() => toggleModule(res.key, res.permissions)}
                      />
                      <strong>{t(res.labelKey)}</strong>
                    </div>

                    {enabled && (
                      <Row className="mt-2 ps-4">
                        {res.permissions.map((perm) => (
                          <Col md="3" key={perm}>
                            <FormGroup check>
                              <Input
                                type="checkbox"
                                checked={permissions[res.key]?.[perm] || false}
                                onChange={() => togglePermission(res.key, perm)}
                              />
                              <Label check className="text-capitalize">
                                {t(`page.roles.${perm}`)}
                              </Label>
                            </FormGroup>
                          </Col>
                        ))}
                      </Row>
                    )}
                  </div>
                );
              })}
            </Col>
          </Row>
        </CardBody>
      </Card>
    </Container>
  );
};

export default RolePermissionPage;